import { type ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getAccessToken } from './tokenStore';

/**
 * Route guard for dashboard pages.
 *
 * `bootRefresh` runs once before `createRoot().render()` in [main.tsx],
 * so by the time this component renders the in-memory token is either
 * populated (cookie was valid) or null (cookie expired or absent).
 *
 * We re-check on `pageshow` with `event.persisted === true` — that's the
 * browser restoring the SPA from bfcache after a back-navigation; without
 * this hook, a signed-out user could see the previous page's state.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (e.persisted) setTick((n) => n + 1); // force a re-evaluation
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  // `tick` is read so React doesn't strip it as unused; the value itself
  // doesn't matter — we just want the component to re-render.
  void tick;

  if (!getAccessToken()) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
